import React from "react";
import { Row, Col, Form, Button } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AdnAvatarUpload from "../common/adnAvatarUpload/AdnAvatarUpload";
import AdnInput from "../common/adnInput/AdnInput";
import AdnDatePicker from "../common/adnDatePicker/AdnDatePicker";
import AdnFormOptions from "../common/adnFormOptions/AdnFormOptions";
import { AdnPhoneInput } from "../common/adnPhoneInput/AdnPhoneInput";
import { useNavigate } from "react-router-dom";
import { dateFormat, NameValidation } from "../common/GeneralConfigurations";
import moment from "moment";
import {
  AdmissionDateValidation,
  BirthDateValidation,
  ResignationDateValidation,
} from "./EmployeeValidations";
import AdnDropdown from "../common/adnDropdown/AdnDropdown";
import { requiredValidation } from "../common/adnCommonValidations/adnCommonValidations";
import AdnEmail from "../common/adnEmail/AdnEmail";
import { AppRoutes } from "../../AppRoutes";
import { AdnInternalEmail } from "../login/LoginValidations";
import AdnAutoComplete from "../common/adnAutoComplete/AdnAutoComplete";
import "./EmployeeInternalDetailsForm.css";
import { X } from "react-feather";
import CaptionComponent from "../common/adnCaptionComponent/AdnCaptionComponent";

function EmployeeInternalDetailsForm({
  form,
  onFinish,
  initialValues,
  disabledDateBirthDate,
  handleBlurName,
  handleBlurEmail,
  workPlaceList,
  submitLoading,
  countryList,
  handleSearchReferenceBy,
  handleSelectReferenceBy,
  referenceByEmployees,
  referenceByName,
  referenceByBlur,
  isEditEnabled,
  image,
  saveImage,
  imgLoading,
  saveEditChanges,
  setIsEditEnabled,
  managerEditPermission,
  disableEdit,
  hasResignationDate,
  handleResignationDateChange,
  resignationReasonOptions,
  hasCorpDepartureReason,
  departmentList,
}) {
  const { t } = useTranslation();

  const navigate = useNavigate();

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        onFinish={onFinish}
        initialValues={initialValues}
        onFieldsChange={disableEdit}
      >
        <Row gutter={14}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 4 }}
            xl={{ span: 4 }}
            xxl={{ span: 4 }}
          >
            <AdnAvatarUpload
              disabled={!isEditEnabled}
              imageUrl={image}
              saveImage={saveImage}
              loading={imgLoading}
            />
          </Col>

          <Col
            className={"internalEditButtonMediaQuery"}
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 20 }}
            xl={{ span: 20 }}
            xxl={{ span: 20 }}
            offset={0}
            style={{ display: "flex", justifyContent: "flex-end" }}
          >
            {saveEditChanges ? (
              <Row style={{ height: "fit-content" }}>
                <CaptionComponent
                  text={t("employeeInternalDetails.buttons.saveChanges")}
                  status={2}
                  isBold={true}
                />
              </Row>
            ) : (
              <Row justify="end" gutter={12}>
                <Col>
                  <Button
                    onClick={() => navigate(AppRoutes.consultantList.path)}
                    disabled={managerEditPermission}
                    className="adn-outlined-button outlined-cancel"
                  >
                    Go to consultants
                  </Button>
                </Col>
                <Col>
                  <Button
                    type="primary"
                    onClick={() => setIsEditEnabled(!isEditEnabled)}
                    disabled={managerEditPermission}
                    className="adn-primary-button primary-cancel"
                  >
                    {" "}
                    {isEditEnabled ? <X /> : <EditOutlined />}
                    {isEditEnabled
                      ? t("employeeInternalDetails.buttons.cancel")
                      : t("employeeInternalDetails.buttons.edit")}
                  </Button>
                </Col>
              </Row>
            )}
          </Col>
        </Row>

        <Row gutter={14}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
          >
            <AdnInput
              name="name"
              label={t("employeeInternalDetails.labels.name")}
              rules={NameValidation}
              placeholder={t(
                "employeeInternalDetails.placeholders.insertInternalName"
              )}
              maxLength={100}
              handleBlur={handleBlurName}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
          >
            <AdnEmail
              handleBlur={handleBlurEmail}
              validationType={AdnInternalEmail}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
        </Row>

        <Row gutter={14}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
            key={"phone"}
          >
            <AdnPhoneInput
              name="phone"
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
            key={"phone2"}
          >
            <AdnPhoneInput
              name="phone2"
              label={t("employeeFormDetails.labels.phone2")}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
        </Row>

        <Row gutter={14}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
          >
            <AdnDatePicker
              name="birthDate"
              label={t("employeeInternalDetails.labels.birthDate")}
              rules={BirthDateValidation}
              defaultPickerValue={moment("1995-01-01", dateFormat)}
              disabledDate={disabledDateBirthDate}
              format={dateFormat}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
          >
            <AdnDropdown
              name="gender"
              label={t("employeeInternalDetails.labels.gender")}
              type="text"
              rules={[
                {
                  required: true,
                  message: t(
                    "employeeInternalDetails.validation.genderRequired"
                  ),
                },
              ]}
              placeholder={t(
                "employeeInternalDetails.placeholders.selectGender"
              )}
              options={[
                { id: "M", name: t("employeeInternalDetails.options.male") },
                { id: "F", name: t("employeeInternalDetails.options.female") },
              ]}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
        </Row>

        <Row gutter={14}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
          >
            <AdnDropdown
              name="workPlaceId"
              label={t("employeeInternalDetails.labels.location")}
              type="text"
              placeholder={t(
                "employeeInternalDetails.placeholders.selectLocation"
              )}
              options={workPlaceList}
              rules={requiredValidation}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
          >
            <AdnDropdown
              name="nationalityId"
              label={t("employeeInternalDetails.labels.nationality")}
              type="text"
              placeholder={t(
                "employeeInternalDetails.placeholders.selectNationality"
              )}
              options={countryList}
              //disabled={!isEditEnabled}
              disabled="false"
              rules={[
                {
                  required: true,
                  message: t(
                    "employeeInternalDetails.validation.nationalityRequired"
                  ),
                },
              ]}
            />
          </Col>
        </Row>

        <Row gutter={14}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
          >
            <AdnDatePicker
              name="admissionDate"
              label={t("employeeInternalDetails.labels.admissionDate")}
              rules={AdmissionDateValidation(form)}
              format={dateFormat}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 12 }}
            xl={{ span: 12 }}
            xxl={{ span: 12 }}
          >
            <AdnInput
              name="number"
              label={t("employeeConsultantDetails.labels.consultantNumber")}
              placeholder="Insert Consultant nr."
              maxLength={30}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 8 }}
            xl={{ span: 8 }}
            xxl={{ span: 8 }}
          >
            <AdnDatePicker
              name="resignationDate"
              label={t("employeeInternalDetails.labels.resignationDate")}
              rules={ResignationDateValidation(form)}
              format={dateFormat}
              onChange={handleResignationDateChange}
              //disabled={!isEditEnabled}
              disabled="false"
            />
          </Col>
          {hasResignationDate && hasCorpDepartureReason && (
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 12 }}
              lg={{ span: 12 }}
              xl={{ span: 12 }}
              xxl={{ span: 12 }}
            >
              <AdnInput
                name="departureReason"
                label={t("employeeInternalDetails.labels.resignationReason")}
                placeholder={t("internalForm.placeholders.selectReason")}
                maxLength={255}
                //disabled={!isEditEnabled}
                disabled="false"
              />
            </Col>
          )}
          {hasResignationDate && !hasCorpDepartureReason && (
            <Col
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 12 }}
              lg={{ span: 12 }}
              xl={{ span: 12 }}
              xxl={{ span: 12 }}
            >
              {/*<AdnDropdown*/}
              {/*    name="resignationReasonId"*/}
              {/*    label="Resignation Reason"*/}
              {/*    type="text"*/}
              {/*    rules={[{ required: true, message: "Reason is required" }]}*/}
              {/*    options={resignationReasonOptions}*/}
              {/*    placeholder={t("internalForm.placeholders.selectReason")}*/}
              {/*    disabled={!isEditEnabled}*/}
              {/*/>*/}
              <AdnInput
                name="resignationReasonId"
                label={t("employeeInternalDetails.labels.resignationReason")}
                placeholder={t("internalForm.placeholders.selectReason")}
                maxLength={255}
                //disabled={!isEditEnabled}
                disabled="false"
              />
            </Col>
          )}
        </Row>

        <Row gutter={14}>
          <Col
            xs={{ span: 24 }}
            sm={{ span: 24 }}
            md={{ span: 24 }}
            lg={{ span: 8 }}
            xl={{ span: 8 }}
            xxl={{ span: 8 }}
          >
            <Form.Item label={t("internalForm.labels.referencedBy")}>
              <AdnAutoComplete
                placeholder={t("internalForm.placeholders.searchConsultant")}
                handleSearch={handleSearchReferenceBy}
                handleSelect={handleSelectReferenceBy}
                resultList={referenceByEmployees}
                valueId={"name"}
                value={referenceByName}
                defaultValue={referenceByName}
                handleBlur={referenceByBlur}
                disabled={!isEditEnabled}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={14}>
            {/*<Col*/}
            {/*    xs={{ span: 24 }}*/}
            {/*    sm={{ span: 24 }}*/}
            {/*    md={{ span: 24 }}*/}
            {/*    lg={{ span: 8 }}*/}
            {/*    xl={{ span: 8 }}*/}
            {/*    xxl={{ span: 8 }}*/}
            {/*>*/}
            {/*    <AdnDropdown*/}
            {/*        name="departmentId"*/}
            {/*        label={t("employeeConsultantDetails.labels.department")}*/}
            {/*        placeholder={t(*/}
            {/*            "employeeConsultantDetails.placeholders.chooseDepartment"*/}
            {/*        )}*/}
            {/*        disabled={!isEditEnabled}*/}
            {/*        options={departmentList}*/}
            {/*    />*/}
            {/*</Col>*/}
            <Col
                xs={{ span: 24 }}
                sm={{ span: 24 }}
                md={{ span: 24 }}
                lg={{ span: 8 }}
                xl={{ span: 8 }}
                xxl={{ span: 8 }}
            >
                <Row className="detail-title" align="middle">
                    <p>
                        {t("employeeConsultantDetails.labels.primaveraDepartment")}
                    </p>
                </Row>
                {initialValues &&
                    initialValues.primaveraDepartmentDescription && (
                        <Row className="detail-value">
                            <p>
                                {initialValues.primaveraDepartmentDescription}
                            </p>
                        </Row>
                    )}
            </Col>
        </Row>
        <br />
        <br />
        {isEditEnabled && (
          <AdnFormOptions
            handleCancel={() => {
              navigate(AppRoutes.consultantList.path);
            }}
            loading={submitLoading}
            canceltext={t("internalForm.buttons.goToConsultants")}
          />
        )}
      </Form>
    </>
  );
}

export default EmployeeInternalDetailsForm;
